import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SectionHero } from '../components/sections/SectionHero';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Phone, Users, Image as ImageIcon, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { trackWhatsAppLead } from '../lib/trackLead';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// NUTS Logo URLs - Transparent PNG
const NUTS_LOGO = 'https://customer-assets.emergentagent.com/job_c71ccf0b-f871-4091-bddc-a51aa7862b88/artifacts/h6y7dxee_LOGO%20NUTS%20ANDORRA%20TRANSPARENT.png'; // Transparent
const NUTS_LOGO_WITH_UGOLF = 'https://aravell-golf.emergent.host/api/documents/file/doc_bf77c9e049fe'; // White background

// Carousel images per language
const CAROUSEL_IMAGES = {
  ca: [
    'https://aravell-golf.emergent.host/api/documents/file/doc_d20032e01506',
    'https://aravell-golf.emergent.host/api/documents/file/doc_6f5210b1ec14',
    'https://aravell-golf.emergent.host/api/documents/file/doc_8f49880b8136',
    'https://aravell-golf.emergent.host/api/documents/file/doc_ddc02f04d0b3',
    'https://aravell-golf.emergent.host/api/documents/file/doc_385b85750c8a'
  ],
  es: [
    'https://aravell-golf.emergent.host/api/documents/file/doc_95d13c5caa60',
    'https://aravell-golf.emergent.host/api/documents/file/doc_2ed78cfbba33',
    'https://aravell-golf.emergent.host/api/documents/file/doc_f04ef0b397c5',
    'https://aravell-golf.emergent.host/api/documents/file/doc_9b959e7a7b7d',
    'https://aravell-golf.emergent.host/api/documents/file/doc_df6a22438615'
  ],
  fr: [
    'https://aravell-golf.emergent.host/api/documents/file/doc_8025daec9b6a',
    'https://aravell-golf.emergent.host/api/documents/file/doc_52c15195b43d',
    'https://aravell-golf.emergent.host/api/documents/file/doc_f422ec5b3b50',
    'https://aravell-golf.emergent.host/api/documents/file/doc_5b16da8a1397',
    'https://aravell-golf.emergent.host/api/documents/file/doc_62ba015fc555'
  ],
  en: [
    'https://aravell-golf.emergent.host/api/documents/file/doc_f5dfba31cfd4',
    'https://aravell-golf.emergent.host/api/documents/file/doc_64d7f144362d',
    'https://aravell-golf.emergent.host/api/documents/file/doc_f4b811e98a40',
    'https://aravell-golf.emergent.host/api/documents/file/doc_7706b3c27f57',
    'https://aravell-golf.emergent.host/api/documents/file/doc_9efbec2fae42'
  ]
};

const NutsProgramPage = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Get carousel images for current language (fallback to Catalan)
  const carouselImages = CAROUSEL_IMAGES[language] || CAROUSEL_IMAGES.ca;

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  // Reset slide when language changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [language]);

  // Close lightbox on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);

  const defaultTranslations = {
    ca: {
      heroTitle: "Programa d'Iniciació al Golf",
      heroSubtitle: "Descobreix el golf amb NUTS",
      step: "Pas",
      activitiesTitle: "Activitats",
      ctaText: "Contacta amb nosaltres per agendar la teva sessió d'iniciació al golf per només 60€",
      loading: "Carregant...",
      steps: [
        { title: "Pas 1", description: "Un monitor explicant a una parella al camp" },
        { title: "Pas 2", description: "Una classe al camp de pràctiques" },
        { title: "Pas 3", description: "Un jugador al camp" }
      ]
    },
    es: {
      heroTitle: "Programa de Iniciación al Golf",
      heroSubtitle: "Descubre el golf con NUTS",
      step: "Paso",
      activitiesTitle: "Actividades",
      ctaText: "Contacta con nosotros para agendar tu sesión de iniciación al golf por solo 60€",
      loading: "Cargando...",
      steps: [
        { title: "Paso 1", description: "Un monitor explicando a una pareja en el campo" },
        { title: "Paso 2", description: "Una clase en el campo de prácticas" },
        { title: "Paso 3", description: "Un jugador en el campo" }
      ]
    },
    fr: {
      heroTitle: "Programme d'Initiation au Golf",
      heroSubtitle: "Découvrez le golf avec NUTS",
      step: "Étape",
      activitiesTitle: "Activités",
      ctaText: "Contactez-nous pour planifier votre séance d'initiation au golf pour seulement 60€",
      loading: "Chargement...",
      steps: [
        { title: "Étape 1", description: "Un moniteur expliquant à un couple sur le terrain" },
        { title: "Étape 2", description: "Un cours au practice" },
        { title: "Étape 3", description: "Un joueur sur le terrain" }
      ]
    },
    en: {
      heroTitle: "Golf Initiation Program",
      heroSubtitle: "Discover golf with NUTS",
      step: "Step",
      activitiesTitle: "Activities",
      ctaText: "Contact us to schedule your golf initiation session for only 60€",
      loading: "Loading...",
      steps: [
        { title: "Step 1", description: "A coach explaining to a couple on the course" },
        { title: "Step 2", description: "A class at the driving range" },
        { title: "Step 3", description: "A player on the course" }
      ]
    }
  };

  const t = defaultTranslations[language] || defaultTranslations.ca;

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // First try to get content in current language
        let response = await fetch(`${API_URL}/api/nuts-program/${language}`);
        let data = null;
        
        if (response.ok) {
          data = await response.json();
        }
        
        // If no content for this language, try Catalan as fallback
        if (!data || (!data.main_description && !data.steps?.length)) {
          if (language !== 'ca') {
            response = await fetch(`${API_URL}/api/nuts-program/ca`);
            if (response.ok) {
              data = await response.json();
            }
          }
        }
        
        setContent(data);
      } catch (error) {
        console.error('Failed to fetch NUTS program content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [language]);

  const heroTitle = content?.hero_title || t.heroTitle;
  const heroSubtitle = content?.hero_subtitle || t.heroSubtitle;
  const heroImage = content?.hero_image || 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/jdagpogz_ARAVELL_HERO_INICIACIO_1920x700.jpeg';
  
  // Use content steps if available, otherwise use translated defaults
  const defaultSteps = t.steps.map((step, index) => ({
    id: `step_${index + 1}`,
    number: index + 1,
    title: step.title,
    description: step.description,
    image: ''
  }));
  const steps = (content?.steps && content.steps.length > 0) ? content.steps : defaultSteps;
  
  const mainDescription = content?.main_description || '';
  const activitiesTitle = content?.activities_title || t.activitiesTitle;
  const activities = content?.activities || [];
  const ctaText = t.ctaText;
  const ctaLink = "https://wa.me/34628139953?text=Hola%20UGOLF%20ARAVELL%20ANDORRA,%20vull%20informaci%C3%B3%20sobre%20el%20programa%20o%20cursos%20d'iniciaci%C3%B3%20al%20golf";

  if (loading) {
    return (
      <main data-testid="nuts-program-page">
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </main>
    );
  }

  return (
    <main data-testid="nuts-program-page">
      <SEO title={PAGE_SEO.programaNuts.title} description={PAGE_SEO.programaNuts.description} />
      <SectionHero
        image={heroImage}
        title={heroTitle}
        subtitle={heroSubtitle}
      />

      {/* Logo Section */}
      <section className="py-8 bg-[#F9F9F7]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-6"
          >
            <img
              src={NUTS_LOGO}
              alt="NUTS - Programa d'Iniciació al Golf"
              className="h-20 md:h-28"
            />
            
            {/* Mini Carousel - max 1/3 screen width */}
            <div className="w-full max-w-[650px]">
              <div className="relative flex items-center gap-3">
                {/* Left arrow */}
                <button
                  onClick={prevSlide}
                  className="flex-shrink-0 w-10 h-10 bg-[#1A4D2E] hover:bg-[#2A5D3E] rounded-full flex items-center justify-center shadow-md transition-colors"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                
                {/* Carousel container */}
                <div className="flex-1 aspect-video rounded-lg overflow-hidden bg-[#E5E5E5]">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={`${language}-${currentSlide}`}
                      src={carouselImages[currentSlide]}
                      alt={`NUTS Golf ${currentSlide + 1}`}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    />
                  </AnimatePresence>
                </div>
                
                {/* Right arrow */}
                <button
                  onClick={nextSlide}
                  className="flex-shrink-0 w-10 h-10 bg-[#1A4D2E] hover:bg-[#2A5D3E] rounded-full flex items-center justify-center shadow-md transition-colors"
                  aria-label="Següent"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-3">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentSlide ? 'bg-[#1A4D2E]' : 'bg-[#D1D5DB]'
                    }`}
                    aria-label={`Imatge ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section - Two Columns */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            
            {/* Left Column - Description (2/3) */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {/* Main Description */}
                {mainDescription && (
                  <div 
                    className="rich-text text-[#666666] mb-10"
                    dangerouslySetInnerHTML={{ __html: mainDescription }}
                  />
                )}

                {/* Steps with Images */}
                <div className="space-y-8">
                  {steps.map((step, index) => (
                    <motion.div
                      key={step.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex flex-col md:flex-row gap-6 items-start"
                      data-testid={`step-${step.number}`}
                    >
                      {/* Step Image */}
                      <div className="w-full md:w-64 lg:w-72 flex-shrink-0">
                        {step.image ? (
                          <div 
                            className="aspect-video rounded-lg overflow-hidden bg-[#F0F2EF] cursor-pointer group relative"
                            onClick={() => setLightboxImage(step.image)}
                          >
                            <img
                              src={step.image}
                              alt={step.title || `${t.step} ${step.number}`}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        ) : (
                          <div className="aspect-video rounded-lg bg-[#F0F2EF] flex items-center justify-center">
                            <div className="text-center text-[#AAAAAA]">
                              <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                              <span className="text-sm">Imatge {step.number}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Step Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="w-8 h-8 rounded-full bg-[#1A4D2E] text-white text-sm font-bold flex items-center justify-center">
                            {step.number}
                          </span>
                          <h3 className="font-display text-lg font-medium text-[#1A4D2E]">
                            {step.title || `${t.step} ${step.number}`}
                          </h3>
                        </div>
                        <p className="text-[#666666] ml-11">
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right Column - Activities (1/3) */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="lg:sticky lg:top-24"
              >
                <div className="bg-[#F9F9F7] rounded-lg p-6 lg:p-8">
                  <h2 className="font-display text-xl font-medium text-[#1A4D2E] mb-6 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    {activitiesTitle}
                  </h2>
                  
                  {activities && activities.length > 0 ? (
                    <div className="space-y-4">
                      {activities.map((activity, index) => (
                        <div key={activity.id || index} className="group">
                          {activity.image && (
                            activity.link ? (
                              <a 
                                href={activity.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block mb-2"
                              >
                                <img
                                  src={activity.image}
                                  alt={activity.description || `Activitat ${index + 1}`}
                                  className="w-full rounded-lg object-cover hover:opacity-90 transition-opacity"
                                />
                              </a>
                            ) : (
                              <img
                                src={activity.image}
                                alt={activity.description || `Activitat ${index + 1}`}
                                className="w-full rounded-lg object-cover mb-2"
                              />
                            )
                          )}
                          {activity.description && (
                            <p className="text-[#666666] text-sm">
                              {activity.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#888888] text-sm italic">
                      Contingut pròximament disponible.
                    </p>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1A4D2E]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <img
              src={NUTS_LOGO}
              alt="NUTS"
              className="h-20 mx-auto mb-8"
            />
            
            <div className="flex justify-center max-w-2xl mx-auto">
              <a
                href={ctaLink}
                onClick={() => trackWhatsAppLead('academia-nuts')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#1A4D2E] px-8 py-5 text-sm md:text-base font-semibold tracking-wide hover:bg-[#D4AF37] hover:text-white transition-colors text-center max-w-xl"
                data-testid="nuts-cta-btn"
              >
                <Phone className="w-5 h-5 shrink-0" />
                <span>{ctaText}</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setLightboxImage(null)}
          >
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              aria-label="Tancar"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={lightboxImage}
              alt="Imatge ampliada"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default NutsProgramPage;
